import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";

import { listMyGrantTasks, type MyGrantTask } from "@/api/my_grant_tasks";
import { useGrantSuiteNavigation } from "@/context/GrantSuiteNavigationContext";
import { useMunicipality } from "@/context/MunicipalityContext";
import { workflowDeadlineSummary, workflowDeadlineTone } from "@/lib/deadlineDisplay";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const MILESTONE_STAGE_LABELS: Record<string, string> = {
  preparing: "Preparing",
  submitted: "Submitted",
  awarded: "Awarded",
};

type MilestoneGroup = {
  key: number;
  milestoneTitle: string;
  stage: string;
  grantTitle: string;
  opportunityId: number;
  tasks: MyGrantTask[];
};

// Group my assigned tasks under their parent milestone, preserving the server's
// due-date ordering (soonest task first) so the milestone with the nearest work
// surfaces at the top.
function groupTasks(tasks: MyGrantTask[]): MilestoneGroup[] {
  const groups: MilestoneGroup[] = [];
  const byMilestone = new Map<number, MilestoneGroup>();

  for (const task of tasks) {
    let group = byMilestone.get(task.grant_opportunity_milestone_id);
    if (!group) {
      group = {
        key: task.grant_opportunity_milestone_id,
        milestoneTitle: task.milestone.title,
        stage: task.milestone.stage,
        grantTitle: task.grant_opportunity.title,
        opportunityId: task.grant_opportunity_id,
        tasks: [],
      };
      byMilestone.set(group.key, group);
      groups.push(group);
    }
    group.tasks.push(task);
  }

  return groups;
}

function MilestoneRow({
  task,
  onSelect,
}: {
  task: MyGrantTask;
  onSelect: (opportunityId: number) => void;
}) {
  const tone = workflowDeadlineTone(task.due_at);
  const completed = Boolean(task.completed_at);

  return (
    <button
      type="button"
      onClick={() => onSelect(task.grant_opportunity_id)}
      className="flex w-full items-start justify-between gap-3 rounded-md border bg-card px-3 py-2 text-left transition-colors hover:bg-accent"
    >
      <div className="min-w-0">
        <p className={cn("break-words text-sm font-medium", completed && "text-muted-foreground line-through")}>
          {task.title}
        </p>
      </div>
      <span
        className={cn(
          "shrink-0 text-xs",
          tone === "warning" && !completed ? "font-medium text-destructive" : "text-muted-foreground",
        )}
      >
        {workflowDeadlineSummary(task.due_at)}
      </span>
    </button>
  );
}

export function MyMilestonesPanel() {
  const { municipality } = useMunicipality();
  const municipalityId = municipality?.id;
  const { navigateToGrantOpportunity } = useGrantSuiteNavigation();
  const [showCompleted, setShowCompleted] = useState(false);

  const { data: tasks, isLoading, isError } = useQuery({
    queryKey: ["my_grant_tasks", municipalityId, showCompleted],
    queryFn: () => listMyGrantTasks(municipalityId!, { includeCompleted: showCompleted }),
    enabled: Boolean(municipalityId),
  });

  const groups = groupTasks(tasks ?? []);

  return (
    <div className="ordinal-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto" data-testid="my-milestones-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Tasks assigned to you across all grants, grouped by milestone.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Switch
            id="my-milestones-show-completed"
            checked={showCompleted}
            onCheckedChange={setShowCompleted}
            aria-label="Show completed"
          />
          <Label htmlFor="my-milestones-show-completed" className="text-xs text-muted-foreground">
            Show completed
          </Label>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading your milestones…</p>
      ) : isError ? (
        <p className="text-sm text-destructive">Could not load your milestones. Try again shortly.</p>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-10 text-center">
          <CalendarClock className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No open tasks assigned to you.</p>
        </div>
      ) : (
        groups.map((group) => (
          <section key={group.key} className="space-y-2">
            <button
              type="button"
              onClick={() => navigateToGrantOpportunity(group.opportunityId)}
              className="flex w-full items-center justify-between gap-2 text-left"
            >
              <div className="min-w-0">
                {/* Titles wrap instead of truncating (ENG-1983) so the list
                    stays scannable without hover. */}
                <h3 className="break-words text-sm font-semibold">{group.milestoneTitle}</h3>
                <p className="break-words text-xs text-muted-foreground">{group.grantTitle}</p>
              </div>
              <Badge variant="outline" className="shrink-0 font-normal">
                {MILESTONE_STAGE_LABELS[group.stage] ?? group.stage}
              </Badge>
            </button>
            <div className="space-y-2">
              {group.tasks.map((task) => (
                <MilestoneRow key={task.id} task={task} onSelect={navigateToGrantOpportunity} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
